import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Card, Chip, Container, Grid, IconButton, Stack, SwipeableDrawer, TextField, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import Iconify from '../../../../components/Iconify';
import PermissionRequired from '../../../errorPages/permissionRequired';
import { accessVerify, capitalize } from '../../../../utils/common';
import { GLOBAL_LOCATION_ID } from '../../../../utils/constants';
import { getUtilityCategories } from '../../../../redux/slices/utilityRedux/utilityCategoriesRedux';
import { getCashFlowSubCategories } from '../../../../redux/slices/financeRedux/cashFlowRedux';
import {
  addUtilitySupplier,
  deactivateUtilitySupplier,
  getUtilitySuppliers,
  updateUtilitySupplier,
} from '../../../../redux/slices/utilityRedux/utilitySuppliersRedux';

const BILLING_TYPES = [
  { label: 'Metered', value: 'METERED' },
  { label: 'Non Metered', value: 'NON_METERED' },
];

const SUPPLIER_CODE_DUPLICATE_MESSAGE = 'An active utility supplier with this supplier code already exists.';

const createDefaultDataModel = (selectedLocation) => ({
  id: '',
  locationId: selectedLocation?.id && selectedLocation?.id !== GLOBAL_LOCATION_ID ? selectedLocation.id : null,
  utilityCategoryId: null,
  name: '',
  code: '',
  contactNumber: '',
  email: '',
  address: '',
  billingAccountName: '',
  billingAccountNumber: '',
  meterNumber: '',
  billingType: 'NON_METERED',
  expenseLedgerId: null,
  bankAccountName: '',
  bankAccountNumber: '',
  bankName: '',
  bankBranch: '',
  status: 1,
});

export default function UtilitySuppliers() {
  const { themeStretch } = useSettings();
  const { selectedLocation, data: allLocations } = useSelector((state) => state.location);
  const { data, addData } = useSelector((state) => state.utilitySuppliers);
  const { data: utilityCategories } = useSelector((state) => state.utilityCategories);
  const { cashFlowSubCategories } = useSelector((state) => state.cashFlow);

  const [selectedDataObj, setSelectedDataObj] = useState(createDefaultDataModel(selectedLocation));
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState(false);

  const activeCategories = useMemo(() => (utilityCategories || []).filter((value) => Number(value?.status) === 1), [utilityCategories]);
  const administrativeExpenseLedgers = useMemo(() => (cashFlowSubCategories || []).filter((value) => Number(value?.mainCategoryId) === 3), [cashFlowSubCategories]);
  const businessLocations = useMemo(() => (allLocations || []).filter((value) => Number(value?.id) !== GLOBAL_LOCATION_ID), [allLocations]);
  const isGlobalLocation = Number(selectedLocation?.id) === GLOBAL_LOCATION_ID;

  useEffect(() => {
    dispatch(getUtilityCategories());
    dispatch(getCashFlowSubCategories());
  }, []);

  useEffect(() => {
    if (selectedLocation?.id) {
      dispatch(getUtilitySuppliers(selectedLocation.id));
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    if (addData?.data) {
      dispatch(getUtilitySuppliers(selectedLocation?.id));
      setIsModelOpen(false);
      setSelectedDataObj(createDefaultDataModel(selectedLocation));
    }
  }, [addData]);

  const manageModel = (modelData = createDefaultDataModel(selectedLocation), type = 'add') => {
    if (isModelOpen) {
      setIsModelOpen(false);
      setSelectedDataObj(createDefaultDataModel(selectedLocation));
    } else {
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      setSelectedDataObj(type === 'add' ? createDefaultDataModel(selectedLocation) : modelData);
    }
  };

  const updateEditingData = (key, value) => {
    setSelectedDataObj((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'billingType' && value !== 'METERED' ? { meterNumber: '' } : {}),
    }));
  };

  const hasActiveSupplierCodeDuplicate = (supplier) => {
    const code = supplier?.code?.trim().toLowerCase();
    const id = Number(supplier?.id);

    if (!code || Number(supplier?.status) === 0) return false;

    return (data || []).some(
      (value) => Number(value?.status) === 1 && value?.code?.trim().toLowerCase() === code && (!id || Number(value?.id) !== id)
    );
  };

  const handleSubmit = () => {
    if (!selectedDataObj.locationId || Number(selectedDataObj.locationId) === GLOBAL_LOCATION_ID) {
      toast.error('Business location is required!');
      return;
    }

    if (!selectedDataObj.utilityCategoryId) {
      toast.error('Utility category is required!');
      return;
    }

    if (!selectedDataObj.name?.trim()) {
      toast.error('Utility supplier name is required!');
      return;
    }

    if (!selectedDataObj.code?.trim()) {
      toast.error('Utility supplier code is required!');
      return;
    }

    if (!selectedDataObj.billingAccountName?.trim()) {
      toast.error('Utility account / meter name is required!');
      return;
    }

    if (!selectedDataObj.billingType) {
      toast.error('Billing type is required!');
      return;
    }

    if (selectedDataObj.billingType === 'METERED' && !selectedDataObj.meterNumber?.trim()) {
      toast.error('Meter number is required for metered utility suppliers!');
      return;
    }

    if (!selectedDataObj.expenseLedgerId) {
      toast.error('Utility expense ledger is required!');
      return;
    }

    const payload = {
      ...selectedDataObj,
      locationId: Number(selectedDataObj.locationId),
      utilityCategoryId: Number(selectedDataObj.utilityCategoryId),
      name: selectedDataObj.name.trim(),
      code: selectedDataObj.code.trim(),
      contactNumber: selectedDataObj.contactNumber || '',
      email: selectedDataObj.email || '',
      address: selectedDataObj.address || '',
      billingAccountName: selectedDataObj.billingAccountName.trim(),
      billingAccountNumber: selectedDataObj.billingAccountNumber || '',
      meterNumber: selectedDataObj.meterNumber || '',
      billingType: selectedDataObj.billingType,
      expenseLedgerId: Number(selectedDataObj.expenseLedgerId),
      bankAccountName: selectedDataObj.bankAccountName || '',
      bankAccountNumber: selectedDataObj.bankAccountNumber || '',
      bankName: selectedDataObj.bankName || '',
      bankBranch: selectedDataObj.bankBranch || '',
      status: Number(selectedDataObj.status) === 0 ? 0 : 1,
    };

    if (hasActiveSupplierCodeDuplicate(payload)) {
      toast.error(SUPPLIER_CODE_DUPLICATE_MESSAGE);
      return;
    }

    dispatch(isAdd ? addUtilitySupplier(payload) : updateUtilitySupplier(payload));
  };

  const handleStatusChange = (row, nextStatus, event) => {
    event?.stopPropagation();

    const isActivation = Number(nextStatus) === 1;
    const requiredPermission = isActivation ? 'UTILITY_SUPPLIER_UPDATE' : 'UTILITY_SUPPLIER_DELETE';

    if (!accessVerify(requiredPermission)) {
      toast.error(`You do not have permission to ${isActivation ? 'activate' : 'deactivate'} utility suppliers.`);
      return;
    }

    if (window.confirm(`Are you sure you want to ${isActivation ? 'activate' : 'deactivate'} ${row?.original?.name}?`)) {
      if (isActivation) {
        if (hasActiveSupplierCodeDuplicate({ ...row?.original, status: 1 })) {
          toast.error(SUPPLIER_CODE_DUPLICATE_MESSAGE);
          return;
        }

        dispatch(updateUtilitySupplier({ ...row?.original, status: 1 }));
      } else {
        dispatch(deactivateUtilitySupplier({ id: row?.original?.id }));
      }
    }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID' },
    { accessorKey: 'locationName', header: 'Location' },
    { accessorKey: 'utilityCategoryName', header: 'Category' },
    {
      accessorKey: 'name',
      header: 'Supplier',
      Cell: ({ row }) => capitalize(row?.original?.name || ''),
    },
    {
      accessorKey: 'code',
      header: 'Supplier Code',
      Cell: ({ row }) => row?.original?.code || '-',
    },
    {
      accessorKey: 'billingAccountName',
      header: 'Account / Meter',
      Cell: ({ row }) => capitalize(row?.original?.billingAccountName || row?.original?.utilityAccountName || ''),
    },
    {
      accessorKey: 'billingAccountNumber',
      header: 'Account Number',
      Cell: ({ row }) => row?.original?.billingAccountNumber || row?.original?.utilityAccountNumber || '-',
    },
    {
      accessorKey: 'meterNumber',
      header: 'Meter Number',
      Cell: ({ row }) => row?.original?.meterNumber || '-',
    },
    {
      accessorKey: 'billingType',
      header: 'Billing Type',
      Cell: ({ row }) => BILLING_TYPES.find((value) => value.value === row?.original?.billingType)?.label || row?.original?.billingType,
    },
    {
      accessorKey: 'expenseLedgerName',
      header: 'Expense Ledger',
      Cell: ({ row }) => row?.original?.expenseLedgerName || '-',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ row }) =>
        Number(row?.original?.status) === 1 ? (
          <Chip size='small' label='Active' color='success' sx={{ width: 90 }} />
        ) : (
          <Chip size='small' label='Inactive' color='default' sx={{ width: 90 }} />
        ),
    },
  ];

  return (
    <Page title='Utility Suppliers'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Utility Suppliers'
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Administration', href: PATH_DASHBOARD.administration.root },
            { name: 'Utilities' },
            { name: 'Utility Suppliers' },
          ]}
          action={
            accessVerify('UTILITY_SUPPLIER_ADD') ? (
              <Button variant='contained' startIcon={<Iconify icon='eva:plus-fill' />} onClick={() => manageModel(createDefaultDataModel(selectedLocation), 'add')}>
                New Utility Supplier
              </Button>
            ) : null
          }
        />

        {accessVerify('UTILITY_SUPPLIER_VIEW') ? (
          <DataGridTable
            name='Utility Supplier List'
            data={data || []}
            column={columns}
            isLoading={false}
            onRowClick={(row) => {
              if (accessVerify('UTILITY_SUPPLIER_UPDATE')) {
                manageModel(row?.original, 'edit');
              }
            }}
            isRowClickable={accessVerify('UTILITY_SUPPLIER_UPDATE')}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowSelection={false}
            enableRowActions={accessVerify('UTILITY_SUPPLIER_UPDATE') || accessVerify('UTILITY_SUPPLIER_DELETE')}
            renderRowActionItems={(row) => {
              const isActive = Number(row?.original?.status) === 1;

              if (isActive) {
                return [
                  <Tooltip title='Deactivate' key='deactivate'>
                    <span>
                      <IconButton color='error' disabled={!accessVerify('UTILITY_SUPPLIER_DELETE')} onClick={(event) => handleStatusChange(row, 0, event)}>
                        <Iconify icon='solar:trash-bin-minimalistic-outline' width={20} height={20} />
                      </IconButton>
                    </span>
                  </Tooltip>,
                ];
              }

              return [
                <Tooltip title='Activate' key='activate'>
                  <span>
                    <IconButton color='success' disabled={!accessVerify('UTILITY_SUPPLIER_UPDATE')} onClick={(event) => handleStatusChange(row, 1, event)}>
                      <Iconify icon='eva:checkmark-circle-2-fill' width={20} height={20} />
                    </IconButton>
                  </span>
                </Tooltip>,
              ];
            }}
          />
        ) : (
          <PermissionRequired />
        )}

        <SwipeableDrawer
          anchor='top'
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => manageModel()}
          PaperProps={{
            sx: {
              width: '94%',
              height: '92%',
              marginLeft: '3%',
              marginTop: '2%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <h1>{isAdd ? 'Add' : 'Edit'} Utility Supplier</h1>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8}>
              <Stack spacing={3} sx={{ p: 3 }}>
                <Card sx={{ p: 3 }}>
                  <Typography variant='subtitle1' padding='10px 0'>
                    Utility Account / Meter Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        onChange={(event, newValue) => updateEditingData('locationId', newValue?.id || null)}
                        options={businessLocations}
                        value={businessLocations?.find((value) => Number(value?.id) === Number(selectedDataObj?.locationId)) || null}
                        renderInput={(params) => <TextField label='Location' placeholder='Select business location' {...params} />}
                        getOptionLabel={(option) => option?.name || ''}
                        isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                        disabled={!isGlobalLocation}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        onChange={(event, newValue) => updateEditingData('utilityCategoryId', newValue?.id || null)}
                        options={activeCategories}
                        value={activeCategories?.find((value) => Number(value?.id) === Number(selectedDataObj?.utilityCategoryId)) || null}
                        renderInput={(params) => <TextField label='Utility Category' placeholder='Select utility category' {...params} />}
                        getOptionLabel={(option) => option?.name || ''}
                        isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        onChange={(event, newValue) => updateEditingData('billingType', newValue?.value || '')}
                        options={BILLING_TYPES}
                        value={BILLING_TYPES.find((value) => value.value === selectedDataObj?.billingType) || null}
                        renderInput={(params) => <TextField label='Billing Type' placeholder='Select billing type' {...params} />}
                        getOptionLabel={(option) => option?.label || ''}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Utility Account / Meter Name'
                        placeholder='Example: Main electricity meter'
                        value={selectedDataObj?.billingAccountName || selectedDataObj?.utilityAccountName || ''}
                        onChange={(e) => updateEditingData('billingAccountName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Utility Account Number'
                        placeholder='Example: CEB account/customer number'
                        value={selectedDataObj?.billingAccountNumber || selectedDataObj?.utilityAccountNumber || ''}
                        onChange={(e) => updateEditingData('billingAccountNumber', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Meter Number'
                        placeholder='Example: Meter serial number'
                        value={selectedDataObj?.meterNumber || ''}
                        onChange={(e) => updateEditingData('meterNumber', e.target.value)}
                        disabled={selectedDataObj?.billingType !== 'METERED'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        onChange={(event, newValue) => updateEditingData('expenseLedgerId', newValue?.id || null)}
                        options={administrativeExpenseLedgers}
                        value={administrativeExpenseLedgers?.find((value) => Number(value?.id) === Number(selectedDataObj?.expenseLedgerId)) || null}
                        renderInput={(params) => <TextField label='Utility Expense Ledger' placeholder='Select administrative expense ledger' {...params} />}
                        getOptionLabel={(option) => option?.name || ''}
                        isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                      />
                    </Grid>
                  </Grid>
                </Card>

                <Card sx={{ p: 3 }}>
                  <Typography variant='subtitle1' padding='10px 0'>
                    Supplier Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Supplier Name'
                        placeholder='Example: Ceylon Electricity Board - Meter 01'
                        value={selectedDataObj?.name || ''}
                        onChange={(e) => updateEditingData('name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Supplier Code'
                        placeholder='Example: CEB-MTR-01'
                        value={selectedDataObj?.code || ''}
                        onChange={(e) => updateEditingData('code', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Contact Number'
                        placeholder='Example: 0112345678'
                        value={selectedDataObj?.contactNumber || ''}
                        onChange={(e) => updateEditingData('contactNumber', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Email'
                        placeholder='Example: billing@supplier.lk'
                        value={selectedDataObj?.email || ''}
                        onChange={(e) => updateEditingData('email', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label='Address'
                        placeholder='Example: Supplier billing office address'
                        value={selectedDataObj?.address || ''}
                        onChange={(e) => updateEditingData('address', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Card>

                <Card sx={{ p: 3 }}>
                  <Typography variant='subtitle1' padding='10px 0'>
                    Bank / Payment Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Bank Account Name'
                        placeholder='Example: Supplier registered bank account name'
                        value={selectedDataObj?.bankAccountName || selectedDataObj?.accountName || ''}
                        onChange={(e) => updateEditingData('bankAccountName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Bank Account Number'
                        placeholder='Example: Supplier bank account number'
                        value={selectedDataObj?.bankAccountNumber || selectedDataObj?.accountNumber || ''}
                        onChange={(e) => updateEditingData('bankAccountNumber', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Bank Name'
                        placeholder='Example: Commercial Bank'
                        value={selectedDataObj?.bankName || ''}
                        onChange={(e) => updateEditingData('bankName', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Bank Branch'
                        placeholder='Example: Colombo'
                        value={selectedDataObj?.bankBranch || ''}
                        onChange={(e) => updateEditingData('bankBranch', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, m: 3 }}>
                <Stack spacing={3}>
                  <LoadingButton type='submit' variant='contained' size='large' style={{ width: '100%' }} onClick={handleSubmit}>
                    {isAdd ? 'Add Utility Supplier' : 'Save Changes'}
                  </LoadingButton>
                  <Button color='info' variant='outlined' size='large' style={{ width: '100%' }} onClick={() => manageModel()}>
                    Close
                  </Button>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </SwipeableDrawer>
      </Container>
    </Page>
  );
}
